/**
 * Recipient Search Store
 *
 * Zustand store for managing recipient search state.
 * Handles filters, query, sorting, and UI state.
 */
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import {
  DEFAULT_RECIPIENT_FILTERS,
  type IdentifierType,
  type RecipientSearchFilters,
  type RecipientSortOption,
} from '@/types/recipient-search'

interface RecipientSearchState {
  // Search state
  query: string
  /** Immediate input value for display (e.g., page title). Updated on every keystroke. */
  displayQuery: string
  filters: RecipientSearchFilters
  identifierType: IdentifierType | null

  // UI state
  filterPanelOpen: boolean
  isSearching: boolean

  // Parent/subsidiary navigation state (for breadcrumbs)
  parentRecipient: string | undefined
  parentRecipientName: string | undefined

  // Actions
  setQuery: (query: string) => void
  setDisplayQuery: (query: string) => void
  setIdentifierType: (type: IdentifierType | null) => void
  clearIdentifierType: () => void
  setFilters: (filters: Partial<RecipientSearchFilters>) => void
  setSort: (sort: RecipientSortOption) => void
  resetFilters: () => void
  clearAllFilters: () => void
  toggleFilterPanel: () => void
  setFilterPanelOpen: (open: boolean) => void
  setIsSearching: (isSearching: boolean) => void

  // Filter-specific setters
  toggleCertification: (
    cert: keyof Pick<
      RecipientSearchFilters,
      | 'woman_owned'
      | 'veteran_owned'
      | 'sdvosb'
      | 'is_8a'
      | 'hubzone'
      | 'sdb'
      | 'minority_owned'
      | 'small_business'
      | 'alaska_native'
      | 'native_american'
      | 'nonprofit'
      | 'educational'
    >,
  ) => void
  addState: (state: string) => void
  removeState: (state: string) => void
  addNaicsCode: (code: string) => void
  removeNaicsCode: (code: string) => void
  addPscCode: (code: string) => void
  removePscCode: (code: string) => void
  addAgency: (agency: string) => void
  removeAgency: (agency: string) => void
  setSpendingRange: (min?: number, max?: number) => void
  setAwardsRange: (min?: number, max?: number) => void
  setLastAwardDateRange: (min?: string, max?: string) => void
  setParentRecipient: (hash: string | undefined, name?: string) => void
  setHasParent: (hasParent: boolean | undefined) => void
  clearParentRecipient: () => void

  // URL hydration (batched update for performance)
  hydrateFromUrl: (params: {
    filters: Partial<RecipientSearchFilters>
    query: string
    identifierType: IdentifierType | null
    parentRecipient?: string
    parentRecipientName?: string
  }) => void

  // Computed
  getActiveFilterCount: () => number
  hasActiveFilters: () => boolean
}

export const useRecipientSearchStore = create<RecipientSearchState>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      query: '',
      displayQuery: '',
      filters: { ...DEFAULT_RECIPIENT_FILTERS },
      identifierType: null,
      filterPanelOpen: false,
      isSearching: false,
      parentRecipient: undefined,
      parentRecipientName: undefined,

      // Actions
      setQuery: (query) =>
        set((state) => {
          state.query = query
          state.filters.query = query || undefined
        }),

      setDisplayQuery: (query) =>
        set((state) => {
          state.displayQuery = query
        }),

      setIdentifierType: (type) =>
        set((state) => {
          state.identifierType = type
          state.filters.identifier_type = type ?? undefined
        }),

      clearIdentifierType: () =>
        set((state) => {
          state.identifierType = null
          state.filters.identifier_type = undefined
        }),

      setFilters: (filters) =>
        set((state) => {
          Object.assign(state.filters, filters)
        }),

      setSort: (sort) =>
        set((state) => {
          state.filters.sort = sort
        }),

      resetFilters: () =>
        set((state) => {
          state.filters = { ...DEFAULT_RECIPIENT_FILTERS }
          state.query = ''
          state.displayQuery = ''
          state.identifierType = null
        }),

      clearAllFilters: () =>
        set((state) => {
          const { sort, limit } = state.filters
          state.filters = { ...DEFAULT_RECIPIENT_FILTERS, sort, limit }
          state.query = ''
          state.displayQuery = ''
          state.identifierType = null
          // Clear parent recipient state to sync UI with cleared filters
          state.parentRecipient = undefined
          state.parentRecipientName = undefined
        }),

      toggleFilterPanel: () =>
        set((state) => {
          state.filterPanelOpen = !state.filterPanelOpen
        }),

      setFilterPanelOpen: (open) =>
        set((state) => {
          state.filterPanelOpen = open
        }),

      setIsSearching: (isSearching) =>
        set((state) => {
          state.isSearching = isSearching
        }),

      // Certification toggles
      toggleCertification: (cert) =>
        set((state) => {
          const current = state.filters[cert]
          // Toggle: undefined -> true -> undefined
          state.filters[cert] = current === true ? undefined : true
        }),

      // Location filters
      addState: (stateCode) =>
        set((state) => {
          if (!state.filters.states.includes(stateCode)) {
            state.filters.states.push(stateCode)
          }
        }),

      removeState: (stateCode) =>
        set((state) => {
          state.filters.states = state.filters.states.filter(
            (s) => s !== stateCode,
          )
        }),

      // Industry filters
      addNaicsCode: (code) =>
        set((state) => {
          if (!state.filters.naics_codes.includes(code)) {
            state.filters.naics_codes.push(code)
          }
        }),

      removeNaicsCode: (code) =>
        set((state) => {
          state.filters.naics_codes = state.filters.naics_codes.filter(
            (c) => c !== code,
          )
        }),

      addPscCode: (code) =>
        set((state) => {
          if (!state.filters.psc_codes.includes(code)) {
            state.filters.psc_codes.push(code)
          }
        }),

      removePscCode: (code) =>
        set((state) => {
          state.filters.psc_codes = state.filters.psc_codes.filter(
            (c) => c !== code,
          )
        }),

      addAgency: (agency) =>
        set((state) => {
          if (!state.filters.agencies.includes(agency)) {
            state.filters.agencies.push(agency)
          }
        }),

      removeAgency: (agency) =>
        set((state) => {
          state.filters.agencies = state.filters.agencies.filter(
            (a) => a !== agency,
          )
        }),

      // Financial filters
      setSpendingRange: (min, max) =>
        set((state) => {
          state.filters.min_spending = min
          state.filters.max_spending = max
        }),

      setAwardsRange: (min, max) =>
        set((state) => {
          state.filters.min_awards = min
          state.filters.max_awards = max
        }),

      setLastAwardDateRange: (min, max) =>
        set((state) => {
          state.filters.min_last_award_date = min
          state.filters.max_last_award_date = max
        }),

      // Parent/subsidiary filters
      setParentRecipient: (hash, name) =>
        set((state) => {
          state.filters.parent_recipient_hash = hash
          state.parentRecipient = hash
          state.parentRecipientName = name
          // Clear has_parent to avoid contradictory filter state
          // (e.g., user had "Parent Companies Only" selected, then clicked "Subsidiaries")
          state.filters.has_parent = undefined
        }),

      clearParentRecipient: () =>
        set((state) => {
          state.filters.parent_recipient_hash = undefined
          state.parentRecipient = undefined
          state.parentRecipientName = undefined
        }),

      setHasParent: (hasParent) =>
        set((state) => {
          state.filters.has_parent = hasParent
        }),

      // URL hydration - single batched update to avoid multiple re-renders
      hydrateFromUrl: (params) =>
        set((state) => {
          // Apply filters
          Object.assign(state.filters, params.filters)

          // Apply query
          state.query = params.query
          state.displayQuery = params.query
          state.filters.query = params.query || undefined

          // Apply identifier type
          state.identifierType = params.identifierType
          state.filters.identifier_type = params.identifierType ?? undefined

          // Apply parent recipient
          if (params.parentRecipient) {
            state.filters.parent_recipient_hash = params.parentRecipient
            state.parentRecipient = params.parentRecipient
            state.parentRecipientName = params.parentRecipientName
            state.filters.has_parent = undefined
          }
        }),

      // Computed values
      getActiveFilterCount: () => {
        const { filters } = get()
        let count = 0

        if (filters.states.length > 0) count += filters.states.length
        if (filters.cities.length > 0) count += filters.cities.length
        if (filters.naics_codes.length > 0) count += filters.naics_codes.length
        if (filters.psc_codes.length > 0) count += filters.psc_codes.length
        if (filters.agencies.length > 0) count += filters.agencies.length
        if (filters.set_asides.length > 0) count += filters.set_asides.length
        if (filters.fiscal_years.length > 0)
          count += filters.fiscal_years.length
        if (filters.woman_owned) count++
        if (filters.veteran_owned) count++
        if (filters.sdvosb) count++
        if (filters.is_8a) count++
        if (filters.hubzone) count++
        if (filters.sdb) count++
        if (filters.minority_owned) count++
        if (filters.small_business) count++
        if (filters.alaska_native) count++
        if (filters.native_american) count++
        if (filters.nonprofit) count++
        if (filters.educational) count++
        if (filters.min_spending !== undefined) count++
        if (filters.max_spending !== undefined) count++
        if (filters.min_awards !== undefined) count++
        if (filters.max_awards !== undefined) count++
        if (filters.min_last_award_date) count++
        if (filters.max_last_award_date) count++
        if (filters.parent_recipient_hash) count++
        if (filters.has_parent !== undefined) count++

        return count
      },

      hasActiveFilters: () => {
        return get().getActiveFilterCount() > 0
      },
    })),
    { name: 'recipient-search-store' },
  ),
)
